import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, FolderTree } from 'lucide-react';
import { api, type FeedIngestion, type FeedJourney } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDate, timeAgo } from '../../lib/utils';
import { IngestionPill, isMoving } from './IngestionPill';

/**
 * Where one arrival went — the reverse of a run's drawer. The run drawer starts from a run and lists
 * what it read; this starts from what arrived and lists what it became: each project it reached, why
 * routing sent it there, and the pages written from it, each one link away.
 *
 * Re-read while the item is still moving, so a page opened on something waiting turns into its
 * outcome without a reload.
 */
export function Journey({
  feedItemId,
  ingestion,
  project,
}: {
  feedItemId: string;
  ingestion: FeedIngestion | undefined;
  /** The project the item waits in — whose batch window decides when it is read. */
  project?: { slug: string; name: string } | null;
}) {
  const [journey, setJourney] = useState<FeedJourney | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [releasing, setReleasing] = useState(false);
  // Links lead into other sections, each behind its own flag: a link to a section someone cannot
  // open would only bounce them back, so it is not offered — the names stay, as plain text.
  const { hasFeature } = useAuth();
  const canOpenRuns = hasFeature('ACTIVITY');
  const canOpenPages = hasFeature('KB');
  const canOpenSettings = hasFeature('INTEGRATIONS');
  // What the poll checks: the latest state seen, not the one this effect started with.
  const latest = useRef<FeedIngestion | undefined>(ingestion);

  useEffect(() => {
    let cancelled = false;
    latest.current = ingestion;
    setJourney(null);
    setError(null);
    const load = () =>
      api.feed
        .journey(feedItemId)
        .then((next) => {
          if (cancelled) return;
          latest.current = next.ingestion;
          setJourney(next);
        })
        .catch((e: Error) => !cancelled && setError(e.message));
    void load();
    const timer = setInterval(() => {
      if (isMoving(latest.current)) void load();
    }, 10_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [feedItemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const state = journey?.ingestion ?? ingestion;
  // Waiting more than an hour for a batch: the project may simply be set to read once a day. Said
  // here, beside the wait, because nobody looks for a project setting to explain a feed item.
  const longWait =
    state?.state === 'waiting' && state.nextWindowAt !== null && new Date(state.nextWindowAt).getTime() - Date.now() > 60 * 60_000;

  return (
    <section className="rounded-lg border border-border bg-surface-subtle px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">Where it went</h3>
        <IngestionPill ingestion={state} />
        <span className="min-w-0 flex-1 text-[11px] text-ink-muted">{state?.detail}</span>
        {state?.runId && canOpenRuns && (
          <Link
            to={`/activity?run=${encodeURIComponent(state.runId)}`}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-orange-600 hover:underline dark:text-orange-400"
          >
            Open the run <ArrowRight size={11} />
          </Link>
        )}
      </div>

      {longWait && (
        <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">
          Need things read sooner? A project can absorb every 15 minutes or hourly instead of once a day — change{' '}
          <span className="font-medium text-ink-secondary">Absorb</span> under{' '}
          {project && canOpenSettings ? (
            <Link
              to={`/p/${project.slug}/settings/integrations`}
              className="font-medium text-orange-600 hover:underline dark:text-orange-400"
            >
              When arrivals are absorbed
            </Link>
          ) : (
            <span className="font-medium text-ink-secondary">When arrivals are absorbed</span>
          )}{' '}
          in {project ? `${project.name}’s` : 'the project’s'} Integrations settings.
        </p>
      )}

      {state?.releasable && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-ink-muted">
          <span>A rule that caught something it should not have can let this one through.</span>
          <button
            disabled={releasing}
            onClick={() => {
              setReleasing(true);
              setError(null);
              api.feed
                .release(feedItemId)
                .then(() => api.feed.journey(feedItemId))
                .then((next) => {
                  latest.current = next.ingestion;
                  setJourney(next);
                })
                .catch((e: Error) => setError(e.message))
                .finally(() => setReleasing(false));
            }}
            className="rounded-md border border-border bg-card px-2 py-0.5 font-medium text-ink-secondary hover:bg-surface-subtle disabled:opacity-50"
          >
            {releasing ? 'Releasing…' : 'Release'}
          </button>
          {hasFeature('INGEST_RULES') && (
            <Link to="/ingest-rules" className="font-medium text-orange-600 hover:underline dark:text-orange-400">
              See the rules
            </Link>
          )}
        </div>
      )}

      {error && <p className="mt-2 text-[11px] text-danger-text">{error}</p>}

      {journey && journey.projects.length > 0 && (
        <div className="mt-3 space-y-2">
          {journey.projects.map((project) => (
            <div key={project.projectId} className="rounded-md border border-border-subtle bg-card px-3 py-2">
              <div className="flex items-center gap-2">
                <FolderTree size={12} className="flex-shrink-0 text-ink-faint" />
                {project.projectSlug && canOpenPages ? (
                  <Link to={`/p/${project.projectSlug}/kb`} className="text-xs font-medium text-ink hover:underline">
                    {project.projectName}
                  </Link>
                ) : (
                  <span className="text-xs font-medium text-ink">{project.projectName}</span>
                )}
                <span className="text-[10px] text-ink-faint">
                  {project.pages.length > 0
                    ? `${project.pages.length} page${project.pages.length === 1 ? '' : 's'}`
                    : 'received it — no page quotes it'}
                </span>
                {project.confidence !== null && (
                  <span className="ml-auto text-[10px] tabular-nums text-ink-faint" title="How sure routing was that it belongs here">
                    {Math.round(project.confidence * 100)}%
                  </span>
                )}
              </div>
              {project.reason && <p className="mt-1 text-[11px] leading-relaxed text-ink-muted">{project.reason}</p>}
              {project.pages.length > 0 && (
                <ul className="mt-1.5 space-y-0.5">
                  {project.pages.map((page) => (
                    <li key={page.nodeId} className="flex items-center gap-1.5 text-[11px]">
                      <FileText size={10} className="flex-shrink-0 text-ink-faint" />
                      {project.projectSlug && canOpenPages ? (
                        <Link
                          to={`/p/${project.projectSlug}/kb/${page.path.split('/').map(encodeURIComponent).join('/')}`}
                          className="truncate text-ink-secondary hover:text-ink hover:underline"
                          title={page.path}
                        >
                          {page.title}
                        </Link>
                      ) : (
                        <span className="truncate text-ink-secondary">{page.title}</span>
                      )}
                      <span className="flex-shrink-0 text-[10px] text-ink-faint">
                        {page.claims} statement{page.claims === 1 ? '' : 's'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {journey && journey.runs.length > 1 && (
        <p className="mt-2 text-[10px] text-ink-faint">
          Read by {journey.runs.length} runs — latest {timeAgo(journey.runs[0]!.queuedAt)}
          {journey.runs[0]!.finishedAt ? `, finished ${formatDate(journey.runs[0]!.finishedAt)}` : ''}
        </p>
      )}
    </section>
  );
}
